import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Flashcard from './Flashcard';
import './FlashcardsPage.css';
import { getWordKey, getGroupId, persistLearningSrsStateToSupabase, srsSnapshotToWordFields } from '../utils/srs';
import { useLanguage } from '../contexts/LanguageContext';
import { supabase } from '../supabaseClient';
import { useData } from '../contexts/DataContext';

const LearnUnmasteredWordsPage = ({ reviewMode = 'unmastered' }) => {
  const { t, isRTL } = useLanguage();
  const { id } = useParams();
  const navigate = useNavigate();

  // Get user session
  const [session, setSession] = useState(null);

  useEffect(() => {
    const getSession = async () => {
      const { data: { session: currentSession } } = await supabase.auth.getSession();
      setSession(currentSession);
    };
    getSession();
  }, []);

  // Use fast data access like StudyAllWordsPage
  const { modules } = useData();

  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);
  const [activeWords, setActiveWords] = useState([]);
  const [currentWordEntry, setCurrentWordEntry] = useState(null); // Temporary holder
  const [learningMode] = useState(true); // Always learning mode
  const [preloadedSRSData, setPreloadedSRSData] = useState({});
  const initialCardSelectedRef = useRef(false);
  const [loadingSRS, setLoadingSRS] = useState(true);
  const [learningLastUndo, setLearningLastUndo] = useState(null);
  const [learningUndoError, setLearningUndoError] = useState('');
  /** Remount Flashcard after undo when same index+word (e.g. only word left). */
  const [learningCardEpoch, setLearningCardEpoch] = useState(0);

  const handleFlipCard = useCallback(() => {
    setIsFlipped(prev => !prev);
  }, []);

  // SRS weighting logic (exactly like FlashcardsPage)
  const getOptimalWeight = useCallback((srs) => {
    const now = Date.now();
    const isDue = srs.nextDue <= now;
    const recentMistakes = (srs.history || []).slice(-3).filter(h => !h.result).length;
    const interval = srs.learning_interval;
    let weight = 1;
    if (isDue) weight *= 4;
    if (recentMistakes > 0) weight *= 2;
    if (interval < 2) weight *= 1.5;
    if (interval > 10) weight *= 0.3;
    return weight;
  }, []);

  function weightedRandomIndex(weights) {
    const total = weights.reduce((a, b) => a + b, 0);
    let r = Math.random() * total;
    for (let i = 0; i < weights.length; i++) {
      if (r < weights[i]) return i;
      r -= weights[i];
    }
    return weights.length - 1;
  }

  // Safely select and copy a word from the array (optional `wordsList` avoids stale state after undo / queue changes)
  const selectAndCopyWord = useCallback((targetIndex, wordsList) => {
    const list = wordsList ?? activeWords;
    if (list.length === 0) {
      setCurrentWordEntry(null);
      return;
    }

    const safeIndex = Math.max(0, Math.min(targetIndex, list.length - 1));
    const selectedWord = list[safeIndex];

    if (selectedWord) {
      setCurrentWordEntry({ ...selectedWord });
      setCurrentCardIndex(safeIndex);
    } else {
      setCurrentWordEntry(null);
    }
  }, [activeWords]);

  // Fetch all words from all groups and filter for unmastered words
  useEffect(() => {
    const fetchAllUnmasteredWords = async () => {
      // Get user session
      const { data: { session } } = await supabase.auth.getSession();
      const userId = session?.user?.id;

      if (!userId) {
        setLoadingSRS(false);
        return;
      }

      // First, try to find the module from dashboard data
      let currentMod = modules?.find(m => String(m.id) === String(id));

      // If not found in global state (e.g., on refresh or for classroom members), fetch it directly from Supabase
      if (!currentMod) {
        // Check if id is a UUID (contains dashes) or a position (number)
        const isUUID = typeof id === 'string' && id.includes('-');
        
        let query = supabase
          .from('modules_new')
          .select('*');
        
        if (isUUID) {
          // It's a module ID (classroom module) - RLS will handle access check
          query = query.eq('id', id);
        } else {
          // It's a position (private module)
          query = query.eq('position', Number(id))
                      .eq('user_id', userId);
        }
        
        const { data: moduleData, error: moduleError } = await query.single();

        if (moduleError) {
          console.error('Error fetching module:', moduleError);
          setLoadingSRS(false);
          return;
        }

        currentMod = moduleData;
      }

      if (!currentMod) {
        setLoadingSRS(false);
        return;
      }

      setCurrentModule(currentMod);

      // Keep loading state true until we finish processing
      setLoadingSRS(true);

      try {
        // Fetch all word groups for this module
        const { data: groupsData, error: groupsError } = await supabase
          .from('word_groups')
          .select('*')
          .eq('module_id', currentMod.id)
          .order('position', { ascending: false });

        if (groupsError) {
          console.error('Error fetching word groups:', groupsError);
          setLoadingSRS(false);
          return;
        }

        // Fetch all words for all groups with pagination to get ALL words
        const groupIds = groupsData.map(g => g.id);
        let allWords = [];

        if (groupIds.length > 0) {
          let from = 0;
          const limit = 1000;
          let hasMore = true;

          while (hasMore) {
            const { data: wordsData, error: wordsError } = await supabase
              .from('words')
              .select(`
                id,
                group_id,
                from_text,
                to_text,
                position,
                created_at,
                updated_at,
                word_srs_data!left (
                  sm2_interval,
                  learning_interval,
                  sm2_ease,
                  learning_ease,
                  next_due,
                  apparent_tier,
                  history,
                  updated_at
                )
              `)
              .in('group_id', groupIds)
              .eq('word_srs_data.user_id', userId)
              .order('position', { ascending: true })
              .range(from, from + limit - 1);

            if (wordsError) {
              console.error('Error fetching words:', wordsError);
              setLoadingSRS(false);
              return;
            }

            if (wordsData && wordsData.length > 0) {
              // Merge SRS data into words
              const mergedWords = wordsData.map(word => {
                const srs = word.word_srs_data?.[0] || null;
                return {
                  ...word,
                  word_srs_data: undefined,
                  sm2_interval: srs?.sm2_interval ?? 0,
                  learning_interval: srs?.learning_interval ?? 0,
                  sm2_ease: srs?.sm2_ease ?? 2.5,
                  learning_ease: srs?.learning_ease ?? 2.5,
                  next_due: srs?.next_due ?? 0,
                  apparent_tier: srs?.apparent_tier ?? 1,
                  history: srs?.history 
                    ? (typeof srs.history === 'string' ? JSON.parse(srs.history) : srs.history)
                    : []
                };
              });
              allWords.push(...mergedWords);
              from += limit;
              hasMore = wordsData.length === limit; // Continue if we got a full batch
            } else {
              hasMore = false;
            }
          }
        }

        const reviewWords = reviewMode === 'unmastered'
          ? allWords.filter(word => (word.apparent_tier || 1) < 5)
          : allWords;


        // Organize words by group for proper groupIdx handling, and deduplicate by word ID
        const wordsByGroupMap = new Map();
        groupsData.forEach((group, groupIdx) => {
          const groupWords = reviewWords.filter(word => word.group_id === group.id);
          groupWords.forEach(word => {
            const id = word.id;
            if (id && !wordsByGroupMap.has(id)) {
              wordsByGroupMap.set(id, { word, groupIdx });
            }
          });
        });
        const wordsByGroup = Array.from(wordsByGroupMap.values());

        setActiveWords(wordsByGroup);

        // Preload SRS data (already merged from word_srs_data)
        const srsData = {};
        wordsByGroup.forEach(({ word, groupIdx }) => {
          const groupId = getGroupId(currentMod, groupIdx);
          const key = getWordKey(word, currentMod.id, groupId);
          const sm2Ease = word.sm2_ease ?? word.ease ?? 2.5;
          const learningEase = word.learning_ease ?? 2.5;

          srsData[key] = {
            sm2_interval: word.sm2_interval ?? 0,
            learning_interval: word.learning_interval ?? 0,
            sm2_ease: sm2Ease,
            learning_ease: learningEase,
            // Keep legacy ease alias mapped from SM2 ease for any callers that still read it
            ease: sm2Ease,
            nextDue: word.next_due ?? 0,
            history: Array.isArray(word.history) ? word.history : (word.history || []),
            apparentTier: word.apparent_tier ?? 1,
            lastUpdated: word.updated_at ? new Date(word.updated_at).getTime() : Date.now()
          };
        });
        setPreloadedSRSData(srsData);
        setLoadingSRS(false);
      } catch (error) {
        console.error('Error fetching unmastered words:', error);
        setLoadingSRS(false);
      }
    };

    fetchAllUnmasteredWords();
  }, [id, modules, reviewMode]);

  // Learning mode keyboard handling (exactly like FlashcardsPage)
  useEffect(() => {
    if (learningMode) {
      let isProcessing = false;
      let lastKeyTime = 0;
      const DEBOUNCE_TIME = 300;
      let processingTimeout = null;

      const handleKeyDown = (e) => {
        // Block ALL key events if we're processing
        if (isProcessing) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }

        // Prevent key repeat events (when holding down a key)
        if (e.repeat) {
          e.preventDefault();
          return;
        }

        // Prevent rapid key presses with time-based debouncing
        const now = Date.now();
        if ((now - lastKeyTime) < DEBOUNCE_TIME) {
          e.preventDefault();
          return;
        }

        // Set processing flag immediately
        isProcessing = true;
        lastKeyTime = now;

        // Clear any existing timeout
        if (processingTimeout) {
          clearTimeout(processingTimeout);
        }

        try {
          if (e.code === 'Space') {
            // Only handle Space globally if it's not on a flashcard element
            const flashcardElement = e.target.closest('.flashcard');
            if (!flashcardElement) {
              e.preventDefault();
              handleFlipCard();
            }
          }
        } catch (error) {
          console.error('Error in key handler:', error);
        }

        // Set timeout to clear processing flag
        processingTimeout = setTimeout(() => {
          isProcessing = false;
        }, DEBOUNCE_TIME);
      };

      window.addEventListener('keydown', handleKeyDown, true);
      return () => {
        window.removeEventListener('keydown', handleKeyDown, true);
        if (processingTimeout) {
          clearTimeout(processingTimeout);
        }
      };
    }
  }, [learningMode, handleFlipCard]);

  // Learning mode keyboard handling for arrow keys (exactly like FlashcardsPage)
  useEffect(() => {
    if (learningMode) {
      let isProcessing = false;
      let lastKeyTime = 0;
      const DEBOUNCE_TIME = 300;
      let processingTimeout = null;

      const handleLearningKeyDown = (e) => {
        // Block ALL key events if we're processing
        if (isProcessing) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }

        // Prevent key repeat events (when holding down a key)
        if (e.repeat) {
          e.preventDefault();
          return;
        }

        // Prevent rapid key presses with time-based debouncing
        const now = Date.now();
        if ((now - lastKeyTime) < DEBOUNCE_TIME) {
          e.preventDefault();
          return;
        }

        // Set processing flag immediately
        isProcessing = true;
        lastKeyTime = now;

        // Clear any existing timeout
        if (processingTimeout) {
          clearTimeout(processingTimeout);
        }

        try {
          if (e.key === 'ArrowRight') {
            e.preventDefault();
            // Right arrow behavior depends on language direction
            if (isRTL) {
              // In RTL: Right arrow = X (didn't know it)
              const xButton = document.querySelector('.flashcard-feedback-btn.x');
              if (xButton) {
                xButton.click();
              }
            } else {
              // In LTR: Right arrow = check (knew it)
              const checkButton = document.querySelector('.flashcard-feedback-btn.check');
              if (checkButton) {
                checkButton.click();
              }
            }
          } else if (e.key === 'ArrowLeft') {
            e.preventDefault();
            // Left arrow behavior depends on language direction
            if (isRTL) {
              // In RTL: Left arrow = check (knew it)
              const checkButton = document.querySelector('.flashcard-feedback-btn.check');
              if (checkButton) {
                checkButton.click();
              }
            } else {
              // In LTR: Left arrow = X (didn't know it)
              const xButton = document.querySelector('.flashcard-feedback-btn.x');
              if (xButton) {
                xButton.click();
              }
            }
          }
        } catch (error) {
          console.error('Error in learning key handler:', error);
        }

        // Set timeout to clear processing flag
        processingTimeout = setTimeout(() => {
          isProcessing = false;
        }, DEBOUNCE_TIME);
      };

      window.addEventListener('keydown', handleLearningKeyDown, true);
      return () => {
        window.removeEventListener('keydown', handleLearningKeyDown, true);
        if (processingTimeout) {
          clearTimeout(processingTimeout);
        }
      };
    }
  }, [learningMode, isRTL]);

  const [currentModule, setCurrentModule] = useState(null);

  const handleLearningRegisterUndo = useCallback((payload) => {
    setLearningLastUndo({
      ...payload,
      previousWordEntry: currentWordEntry
        ? JSON.parse(JSON.stringify(currentWordEntry))
        : null
    });
    setLearningUndoError('');
  }, [currentWordEntry]);

  const handleLearningGoBack = useCallback(async () => {
    if (!learningLastUndo || !session?.user?.id) return;
    const { wordKey, wordId, previousSrs, cardIndex, previousWordEntry } = learningLastUndo;
    setLearningUndoError('');
    try {
      await persistLearningSrsStateToSupabase(wordId, session.user.id, previousSrs);
      const preloaded = {
        ...previousSrs,
        ease: previousSrs.sm2_ease ?? 2.5
      };
      const wf = srsSnapshotToWordFields(previousSrs);
      setPreloadedSRSData(prev => ({ ...prev, [wordKey]: preloaded }));
      setActiveWords(prev => {
        let next;
        const stillInQueue = prev.some(e => e.word.id === wordId);
        if (stillInQueue) {
          next = prev.map(({ word, groupIdx }) =>
            word.id === wordId
              ? { word: { ...word, ...wf }, groupIdx }
              : { word, groupIdx }
          );
        } else if (previousWordEntry) {
          const entry = {
            word: { ...previousWordEntry.word, ...wf },
            groupIdx: previousWordEntry.groupIdx
          };
          const insertAt =
            typeof cardIndex === 'number'
              ? Math.min(Math.max(0, cardIndex), prev.length)
              : prev.length;
          next = [...prev.slice(0, insertAt), entry, ...prev.slice(insertAt)];
        } else {
          next = prev;
        }
        const targetIdx = next.findIndex(e => e.word.id === wordId);
        queueMicrotask(() => {
          if (targetIdx >= 0) {
            selectAndCopyWord(targetIdx, next);
          }
          setLearningCardEpoch(e => e + 1);
        });
        return next;
      });
      setIsFlipped(false);
      setLearningLastUndo(null);
    } catch (err) {
      console.error('Learning undo failed:', err);
      setLearningUndoError(t('errorUpdatingProgress') || 'Failed to update your progress. Please try again.');
    }
  }, [learningLastUndo, session?.user?.id, t, selectAndCopyWord]);

  const handleSRSUpdate = useCallback((wordKey, updatedSRSData) => {
    // Update SRS data first
    setPreloadedSRSData(prev => ({
      ...prev,
      [wordKey]: updatedSRSData
    }));

    // If the word just became mastered (tier 5), remove it from activeWords
    // This now happens during the 200ms transition delay synchronization
    if (reviewMode === 'unmastered' && updatedSRSData.apparentTier >= 5) {
      setActiveWords(prev =>
        prev.filter(({ word, groupIdx }) => {
          const groupId = getGroupId(currentModule, groupIdx);
          const key = getWordKey(word, currentModule.id, groupId);
          return key !== wordKey;
        })
      );
    }
  }, [currentModule, reviewMode]);

  // When learning mode is activated, start with a weighted random card
  useEffect(() => {
    if (learningMode && activeWords.length > 0 && !initialCardSelectedRef.current) {
      // Calculate weights for all words based on their SRS data
      const weights = activeWords.map(({ word, groupIdx }) => {
        const groupId = getGroupId(currentModule, groupIdx);
        const key = getWordKey(word, currentModule.id, groupId);
        const srs = preloadedSRSData?.[key] || {
          sm2_interval: word.sm2_interval ?? 0,
          learning_interval: word.learning_interval ?? 0,
          ease: word.sm2_ease ?? 2.5,
          nextDue: word.next_due ?? 0,
          history: typeof word.history === 'string' ? JSON.parse(word.history) : (word.history || []),
          apparentTier: word.apparent_tier ?? 1,
          lastUpdated: word.updated_at ? new Date(word.updated_at).getTime() : Date.now()
        };
        return getOptimalWeight(srs);
      });

      // Select initial card using weighted random
      const initialIndex = reviewMode === 'random'
        ? Math.floor(Math.random() * activeWords.length)
        : weightedRandomIndex(weights);
      selectAndCopyWord(initialIndex);
      setIsFlipped(false);
      initialCardSelectedRef.current = true;
    }
  }, [learningMode, activeWords, currentModule, preloadedSRSData, getOptimalWeight, selectAndCopyWord, reviewMode]);

  // In learning mode, advance endlessly, no prev/next, no card count
  const handleLearningNext = () => {
    // In learning mode, use weighted random selection based on SRS data
    if (activeWords.length === 0) return;

    // Filter out mastered words before selection to ensure we don't select them
    let eligibleWords = activeWords.filter(({ word, groupIdx }) => {
      const groupId = getGroupId(currentModule, groupIdx);
      const key = getWordKey(word, currentModule.id, groupId);
      const srs = preloadedSRSData?.[key];

      // Also filter out the current word to prevent reselecting it
      const isCurrentWord = currentWordEntry &&
        currentWordEntry.word.id === word.id &&
        currentWordEntry.groupIdx === groupIdx;

      const allowedByMode = reviewMode !== 'unmastered' || !srs || srs.apparentTier < 5;
      return allowedByMode && !isCurrentWord;
    });

    if (eligibleWords.length === 0 && reviewMode !== 'unmastered') {
      eligibleWords = activeWords;
    }
    if (eligibleWords.length === 0) return;

    if (reviewMode === 'random') {
      const nextEntry = eligibleWords[Math.floor(Math.random() * eligibleWords.length)];
      selectAndCopyWord(activeWords.indexOf(nextEntry));
      setIsFlipped(false);
      return;
    }

    // Calculate weights for unmastered words only
    const weights = eligibleWords.map(({ word, groupIdx }) => {
      const groupId = getGroupId(currentModule, groupIdx);
      const key = getWordKey(word, currentModule.id, groupId);
      const srs = preloadedSRSData?.[key] || {
        sm2_interval: word.sm2_interval ?? 0,
        learning_interval: word.learning_interval ?? 0,
        ease: word.sm2_ease ?? 2.5,
        nextDue: word.next_due ?? 0,
        history: typeof word.history === 'string' ? JSON.parse(word.history) : (word.history || []),
        apparentTier: word.apparent_tier ?? 1,
        lastUpdated: word.updated_at ? new Date(word.updated_at).getTime() : Date.now()
      };
      return getOptimalWeight(srs);
    });

    // Select next card using weighted random from unmastered words only
    const nextIndex = weightedRandomIndex(weights);
    // Map back to original activeWords index
    const originalIndex = activeWords.findIndex((item, index) =>
      eligibleWords[nextIndex] === item
    );
    selectAndCopyWord(originalIndex);
    setIsFlipped(false);
  };

  // Show loading if data is still loading
  if (loadingSRS) {
    return (
      <div className="flashcards-page-container">
        <div className="flashcard-view-container">
          <div className="flashcard-skeleton" style={{ height: 320, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, color: '#aaa' }}>
            {t('loading')}
          </div>
        </div>
      </div>
    );
  }

  // Show error if module not found
  if (!currentModule) {
    return (
      <div className="flashcards-page-container">
        <div className="flashcard-view-container">
          <div className="flashcard-skeleton" style={{ height: 320, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, color: '#aaa' }}>
            {t('moduleNotFound') || 'Module not found'}
          </div>
        </div>
      </div>
    );
  }

  // Don't render anything until we have processed the data

  const noUnmasteredWords = activeWords.length === 0;

  if (noUnmasteredWords) {
    return (
      <div className="flashcards-page-container">
        <div className="flashcard-view-container">
          <div className="flashcard-skeleton" style={{
            height: 320,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 24,
            color: '#aaa'
          }}>
            {reviewMode === 'unmastered'
              ? (t('congratulationsAllWordsMastered') || 'Congratulations! You have mastered all of your words!')
              : (t('noWordsYet') || 'No words yet')}
          </div>
          <button
            onClick={() => navigate(`/module/${currentModule?.id || id}`)}
            className="btn btn-primary"
            style={{ marginTop: '1rem' }}
          >
            {t('returnToModule') || 'Return to Module'}
          </button>
          {learningLastUndo ? (
            <div style={{ marginTop: '0.75rem', textAlign: 'center' }}>
              <button
                type="button"
                onClick={handleLearningGoBack}
                className="btn btn-secondary"
                style={{
                  fontSize: '0.8125rem',
                  opacity: 0.75,
                  padding: '0.35rem 0.75rem'
                }}
              >
                {isRTL ? '→' : '←'} {t('sm2UndoLastCard')}
              </button>
              {learningUndoError ? (
                <div style={{ color: 'red', marginTop: '0.5rem', fontSize: '0.85rem' }}>{learningUndoError}</div>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  const currentWord = currentWordEntry?.word;
  const currentGroupIdx = currentWordEntry?.groupIdx;

  // If no current word entry, show loading
  if (!currentWordEntry || !currentWord) {
    return (
      <div className="flashcards-page-container">
        <div className="flashcard-view-container">
          <div className="flashcard-skeleton" style={{ height: 320, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, color: '#aaa' }}>
            {t('loading')}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flashcards-page-container">
      <button onClick={() => navigate(`/module/${currentModule?.id || id}`)} className="btn btn-secondary btn-back">
        {isRTL ? '→' : '←'} {t('exitFlashcards')}
      </button>
      <div className="flashcard-view-container">
        <Flashcard
          key={`${learningCardEpoch}-${currentCardIndex}-${currentWord?.id || ''}`}
          word={currentWord}
          module={currentModule}
          isFlipped={isFlipped}
          onFlip={handleFlipCard}
          moduleId={currentModule.id}
          groupIdx={currentGroupIdx}
          onFeedback={handleLearningNext}
          onSRSUpdate={handleSRSUpdate}
          userId={session?.user?.id}
          preloadedSRSData={preloadedSRSData}
          mode="learning"
          learningUndoCardIndex={currentCardIndex}
          onLearningRegisterUndo={handleLearningRegisterUndo}
        />
        {reviewMode === 'unmastered' && (
          <div className="unmastered-counter" style={{ fontWeight: 'bold', fontSize: '1rem', color: '#555', textAlign: 'center', direction: isRTL ? 'rtl' : 'ltr' }}>
            {t('unmasteredWordsLeft') || 'Unmastered Words Left:'} {activeWords.length}
          </div>
        )}
        {learningLastUndo ? (
          <div
            className="learning-undo-active"
            style={{
              marginTop: '0.75rem',
              textAlign: 'center',
              direction: isRTL ? 'rtl' : 'ltr'
            }}
          >
            <button
              type="button"
              onClick={handleLearningGoBack}
              className="btn btn-secondary"
              style={{
                width: '100%',
                maxWidth: '440px',
                fontWeight: 600,
                fontSize: '1rem',
                padding: '0.7rem 1rem'
              }}
            >
              {isRTL ? '→' : '←'} {t('sm2UndoLastCard')}
            </button>
            {learningUndoError ? (
              <div style={{ color: 'red', marginTop: '0.5rem', fontSize: '0.9rem' }}>{learningUndoError}</div>
            ) : null}
          </div>
        ) : null}
        <div
          className="learning-mode-info"
          style={{
            marginTop: '0.75rem',
            direction: isRTL ? 'rtl' : 'ltr',
            textAlign: 'center'
          }}
        >
          {t('learningModeInfo')}
        </div>
      </div>
    </div>
  );
};

export default LearnUnmasteredWordsPage;
